import clothingData from "./clothing.json";

interface ClothingItem {
  id: number;
  name: string;
  type: string;
  category: string;
  description: string;
  min_temp: number;
  max_temp: number;
  weather_conditions: string[];
  warmth_level: number;
  when_to_wear: string;
  style_tags: string[];
  material_info: string;
  score?: number;
}

interface WeatherInput {
  tempC: number;
  feelsLikeC: number;
  humidity: number;
  windSpeedMs: number;
  conditions: string;
  mainCondition: string;
}

function cToF(c: number) { return Math.round(c * 9 / 5 + 32); }

export function recommend(weather: WeatherInput): { category: string; items: ClothingItem[] }[] {
  const items = clothingData as ClothingItem[];
  const tempF = cToF(weather.feelsLikeC);
  const windMph = weather.windSpeedMs * 2.237;
  const conditions = weather.conditions.toLowerCase();
  const main = weather.mainCondition.toLowerCase();

  const isRainy = conditions.includes("rain") || main === "rain";
  const isSnowy = conditions.includes("snow") || main === "snow";
  const isWindy = windMph > 15;
  const isSunny = conditions.includes("sun") || main === "clear";
  const isCloudy = conditions.includes("cloud") || main === "clouds";
  const isHot = tempF >= 80;
  const isCold = tempF < 50;

  const scored: ClothingItem[] = [];

  for (const item of items) {
    let score = 0;
    const min = item.min_temp ?? -100;
    const max = item.max_temp ?? 200;

    if (tempF >= min && tempF <= max) {
      score += 15;
      const range = max - min;
      if (range > 0) {
        const pos = (tempF - min) / range;
        if (pos > 0.25 && pos < 0.75) score += 5;
      }
    } else if (tempF >= min - 5 && tempF <= max + 5) {
      score += 5;
    } else {
      continue;
    }

    for (const cond of item.weather_conditions || []) {
      const c = cond.toLowerCase();
      if (c === "rain" && isRainy) score += 8;
      if (c === "snow" && isSnowy) score += 8;
      if (c === "windy" && isWindy) score += 6;
      if (c === "sunny" && isSunny) score += 4;
      if (c === "cloudy" && isCloudy) score += 2;
      if (c === "hot" && isHot) score += 5;
      if (c === "cold" && isCold) score += 5;
    }

    if ((isCold || isSnowy) && (item.category === "outerwear" || item.category === "accessories")) score += 2;
    if (isRainy && (item.category === "outerwear" || item.category === "accessories")) score += 2;
    if (isCold && item.warmth_level >= 4) score += 2;
    if (isHot && item.warmth_level <= 2) score += 2;

    if (score >= 18) {
      scored.push({ ...item, score });
    }
  }

  scored.sort((a, b) => (b.score || 0) - (a.score || 0));

  const grouped: Record<string, ClothingItem[]> = {};
  const extreme = isCold || isHot || isRainy || isSnowy;

  for (const item of scored) {
    const cat = item.category;
    if (!grouped[cat]) grouped[cat] = [];
    const maxPer = extreme && (cat === "outerwear" || cat === "accessories") ? 2 : 1;
    if (grouped[cat].length < maxPer) grouped[cat].push(item);
  }

  const order = ["outerwear", "tops", "bottoms", "accessories", "footwear"];
  return order
    .filter((cat) => grouped[cat]?.length)
    .map((cat) => ({ category: cat, items: grouped[cat] }));
}
